<template>
  <div>
    <form role="form" id="private-lessons-form">
      <div class="flex flex-col gap-y-md">
        <label class="flex">
          <input type="checkbox" name="private_lessons_enabled" v-model="enabled" aria-labelledby="enabled-label" aria-describedby="enabled-description">
          <div class="m-l-m flex flex-col">
            <span id="enabled-label" class="font-semi">
              Enable private lessons
            </span>
            <p id="enabled-description" class="font-normal m-b-none">
              Turn on or off the private lesson booking feature for your entire team
            </p>
          </div>
        </label>

        <div class="" v-if="enabled">
          <h4 class="font-semi m-t-none">Minimum notice</h4>
          <div class="form-group flex align-items-start m-t-m">
            <div>
              <input type="number" class="form-control" style="width: 80px" id="minimumNoticeHours" v-model="minimumNoticeHours" min="0">
            </div>
            <div class="m-l-m flex flex-col">
              <label for="minimumNoticeHours" class="font-semi m-t-none m-b-xxs">Hours required before start of lesson</label>
              <p class="font-normal m-b-none m-t-none">
                For example, if you set this to 24, then a parent can book an empty private lesson up to 24 hours before the lesson starts. <strong>Confirmed lessons that have additional open wrestler slots will be available to book until the lesson starts.</strong> Admins can override this and manually book a wrestler to a private lesson at any time.
              </p>
            </div>
          </div>

          <h4 class="font-semi m-t-md">Coach confirmation settings</h4>
          <div class="flex flex-col m-t-sm">
            <label class="flex">
              <input type="radio" name="coachConfirmation" v-model="coachConfirmation" value="auto_confirm" aria-labelledby="auto-confirm-label" aria-describedby="auto-confirm-description">
              <div class="m-l-m flex flex-col">
                <span id="auto-confirm-label" class="font-semi">
                  Auto confirm
                </span>
                <p id="auto-confirm-description" class="font-normal m-b-none">
                  Coaches who are booked for a private lesson will be automatically confirmed for the lesson
                </p>
              </div>
            </label>
            <label class="flex m-t-xs">
              <input type="radio" name="coachConfirmation" v-model="coachConfirmation" value="require_coach_confirmation" aria-labelledby="require-confirmation-label" aria-describedby="require-confirmation-description">
              <div class="m-l-m flex flex-col">
                <span id="require-confirmation-label" class="font-semi">
                  Require coach confirmation
                </span>
                <p id="require-confirmation-description" class="font-normal m-b-none">
                  Coaches who are booked for a private lesson will need to confirm the lesson before it can be billed
                </p>
              </div>
            </label>
          </div>
        </div>
      </div>

      <ladda-button @lbClicked="update" el-class="btn-primary m-t-md" :loading="updating">
        Save
      </ladda-button>
      <div v-if="error">
        <small><span class="text-danger">{{ errorMessage }}</span></small>
      </div>
    </form>

    <!-- Enabling the feature alone doesn't make anything bookable — coaches
         need pricing and the accepting toggle, which live on the command
         center. Point there so admins aren't left guessing at the next step. -->
    <div v-if="savedEnabled" class="bg-blue-50 border border-solid border-blue-300 rounded-sm p-sm m-t-md" data-cy="pl-settings-next-steps">
      <div class="flex gap-x-sm align-items-start">
        <i class="fa-solid fa-circle-arrow-right text-blue-600 m-t-xxs"></i>
        <div class="flex-1">
          <p class="m-b-none font-semi">Next: set up your coaches</p>
          <p class="m-b-none text-14 text-gray-600">
            Enabling the feature doesn't make anything bookable on its own — each coach needs pricing and to be marked as accepting lessons. Coaches add their own availability from their lessons dashboard.
          </p>
        </div>
      </div>
      <a :href="commandCenterPath" class="btn btn-primary w-full m-t-sm" data-cy="pl-settings-setup-coaches">
        Set Up Coaches
      </a>
    </div>
  </div>
</template>

<script>
import LaddaButton from '../../shared/ladda_button.vue';
import { errorableMix } from './mixins/errorable_mix';

export default {
  name: 'private-lesson-settings',
  components: { LaddaButton },
  mixins: [errorableMix],
  props: {
    initialEnabled: {
      type: Boolean,
      required: true,
    },
    initialMinimumNoticeHours: {
      type: Number,
      required: true,
    },
    initialCoachConfirmation: {
      type: String,
      required: true,
    },
    teamId: {
      type: Number,
      required: true,
    },
    commandCenterPath: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      enabled: false,
      minimumNoticeHours: 24,
      coachConfirmation: 'auto_confirm',
      updating: false,
      // Tracks the persisted value, not the checkbox — the next-steps callout
      // should only appear once enabling has actually been saved
      savedEnabled: false,
    };
  },
  mounted() {
    this.enabled = this.initialEnabled;
    this.minimumNoticeHours = this.initialMinimumNoticeHours;
    this.coachConfirmation = this.initialCoachConfirmation;
    this.savedEnabled = this.initialEnabled;
  },
  methods: {
    update: _.throttle(function () {
      if (this.updating) {
        return;
      }

      const vm = this;
      vm.updating = true;
      const url = vm.$apiService.teamUrl(this.teamId);
      const data = {
        team: {
          private_lessons_enabled: this.enabled,
          private_lessons_minimum_notice_hours: this.minimumNoticeHours,
          private_lessons_coach_confirmation: this.coachConfirmation,
        },
      };

      axios.put(url, data)
        .then((response) => {
          vm.$notificationManager.emit('show-toast', 'Private lesson settings saved', true);
          vm.savedEnabled = vm.enabled;
          vm.updating = false;
        })
        .catch((error) => {
          vm.updating = false;
          vm.errorMessage = `Error: ${error.toString()}`;
          vm.error = true;
        });
    }, 500),
  },
};
</script>
